/**
 * Phase 0 の調整値。すべて実寸（メートル・秒）で持つ。
 * 描画側の解像度から独立させておくことで、端末が変わっても挙動が変わらない。
 */
export const TUNING = {
  /** 固定タイムステップ。可変 dt にすると決定論が壊れるので絶対に変えない。 */
  dt: 1 / 60,

  /** 泳ぎ */
  swimSpeed: 2.9, // m/s 岸方向の基本速度
  lateralSpeed: 3.6, // m/s 横移動。前進の 1.5 倍程度に留め、レーン横断に時間がかかるようにする。
  /** 横に振るほど前に進まない。これがないと寄り道が無料になり、宝の配置が意味を失う。 */
  lateralCost: 0.5,

  /** ダイブ（タップで発動、固定時間コミット） */
  diveTime: 1.05, // s 潜っている時間。崩れの先頭が通り抜けるのに要する時間より長くする
  diveCost: 20, // 息ゲージ消費
  diveForwardFactor: 0.72, // 潜行中の前進倍率
  diveLateralFactor: 0.6, // 潜行中の横移動倍率
  breathMax: 100,
  breathRefill: 26, // per s 水面での回復

  /** うねりに乗る */
  faceWindow: 3.6, // m 崩れていない波の斜面と判定される幅。広すぎると乗るのに技術が要らなくなる。
  /**
   * 斜面に乗っている間の速度。波の位相速度（深場で約 8.9 m/s）より必ず遅くすること。
   * 同じ速度にすると乗ったまま浜まで運ばれてしまい、波を読む意味が消える。
   */
  surfSpeed: 5.4,
  /** 掘れ上がった波の斜面に乗っているときの速度倍率。崩れる直前がいちばん速い。 */
  crestSurfBonus: 1.25,
  rideCombo: 0.8, // s これ以上乗り続けるとコンボ加算

  /** 白波 */
  /** 泡の見た目の帯の半幅（m）。当たり判定ではない。 */
  foamVisualHalf: 3.6,
  /**
   * 実際に巻かれる「崩れの先頭」の半幅（m）。危険なのは崩れの先頭だけで、
   * その後ろに残る泡は（現実と同じく）泳いで抜けられる。
   *
   * 不変条件: foamHitHalf < faceWindow
   *   斜面に乗っている位置が、そのまま当たり判定の内側であってはならない。
   *   両方 3.6 m にしていたため「乗っていた波が砕けた瞬間に予告なく被弾する」
   *   という、直前まで加速報酬を与えていたぶん最も理不尽な失敗が起きていた。
   */
  foamHitHalf: 1.3,
  /**
   * 崩れた波が進む最低速度（m/s）。
   *
   * 不変条件: (foamMinSpeed - swimSpeed) >= 2 * foamHitHalf / diveTime
   *   崩れた波を浅水波の位相速度で進めると、浜の近くで泳者とほぼ同速になり、
   *   衝撃帯が数十秒その場に居座る。すると 1 回のダイブでは絶対に抜けられず、
   *   「潜っても避けられない」状態になる（実測で最大 54 秒、最浅部では追い越し不能）。
   *   実際の砕波後の段波は元のうねりより速く走るので、下限を設けるのは物理的にも自然。
   */
  foamMinSpeed: 5.6,
  /**
   * 崩れた瞬間にその波の斜面にいた場合に与える猶予（秒）。
   *
   * 不変条件: breakGrace >= (faceWindow - foamHitHalf) / (foamMinSpeed - swimSpeed)
   *   斜面の最も沖側で崩れた場合に、崩れの先頭が到達するまでの時間を覆う長さが必要。
   *   短いと被弾を防ぐのではなく先延ばしにするだけで、しかも遅れて当たる瞬間には
   *   泡が視覚的に自分を追い越しているため、何が起きたのか分からない被弾になる。
   *   乗っていた波が目の前で崩れるのは読みようがないので、ここは無条件に見逃す。
   *   読むべきなのは「掘れ上がった波（予告あり）に乗り続けるかどうか」の判断。
   */
  breakGrace: 0.95,
  /** 巻かれたあとの無敵時間（秒）。連鎖被弾を防ぐ。 */
  postCatchGrace: 0.6,
  dolphinWindow: 0.42, // s 接触前この時間内に潜り始めていればパーフェクト
  dolphinBoost: 4.2, // m 前進する距離ではなく速度への上乗せ（m/s）
  dolphinBreathRefund: 15,
  caughtStun: 0.95, // s 巻かれて操作不能になる時間
  caughtPushback: 12.0, // m/s 沖へ押し戻される速度
  caughtDropCount: 2, // 巻かれたときに落とす宝の数

  /**
   * 離岸流が沖へ引く速度。
   *
   * 不変条件: ripPush < swimSpeed * (1 - carryDragMax)
   * これを破ると、真っすぐ泳いでいるだけで後退する「通れない壁」になる。
   * ゲームオーバーが存在しない設計では、閉じ込められることが最悪の失敗になる。
   * 現実の対処と同じく「横切って抜ける」のが最適手であってほしいが、
   * 正面突破も（非常に遅いだけで）必ず可能でなければならない。
   */
  ripPush: 1.4, // m/s

  /** 生き物 */
  creatureRadius: 1.6, // m 接触の判定半径
  /** クラゲに刺されて操作不能になる時間（秒）。巻かれるのと違い、押し戻されない。 */
  stingTime: 0.75,
  /** イルカに掴まって加速する時間（秒）と、その間の速度（m/s）。 */
  dolphinBoostTime: 2.2,
  dolphinSpeed: 7.2,
  /** 子ガメを 1 匹連れ帰るごとの加点。 */
  hatchlingBonus: 150,

  /** 宝 */
  pickupRadius: 1.5, // m
  carryDrag: 0.004, // 1個あたりの減速率（積むほど重い）
  carryDragMax: 0.3,

  /** スコア */
  /** 帰還そのものの価値。宝ゼロでもスコアが出るようにして、完走を必ず報う。 */
  finishBonus: 500,
  comboExponent: 1.5,
  comboMultiplier: 5,
} as const;

/** 波が崩れる判定。水深が波高のこの倍率を下回ると崩れる（実際の砕波の目安に近い）。 */
export const BREAK_DEPTH_RATIO = 1.35;

/** 崩れた波が形を取り戻す水深の倍率。BREAK_DEPTH_RATIO より大きくしてヒステリシスにする。 */
export const REFORM_DEPTH_RATIO = 2.0;

/**
 * 「掘れ上がっている」と見なす水深の倍率。BREAK_DEPTH_RATIO より大きい。
 * この状態の波は描画で危険を予告し、乗っていればいちばん速い。
 * 崩れる直前が最高の斜面であり、同時にいちばん危ないという読み合いを作る。
 */
export const CREST_DEPTH_RATIO = 1.7;

/** 浅水波の位相速度に使う重力加速度。水深が浅いほど波は遅くなる。 */
export const GRAVITY = 9.81;
